import React, { useState } from 'react';
import { StyledContainer, StyledPaperContainer } from '../../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import CreateClinicBody from '../../../CreateClinic/components/CreateClinicBody';
import { ClinicFormAndIdProps, ClinicFormProps, ClinicIdProps, ExistingClinicFormProps } from '../../../CreateClinic/types';
import { StyledButtonsContainer } from '../../../../Questionnaires/QuestionnairePage/components/QuestionViewerEditor/QuestionEditor/styles';
import useDeleteClinic from '../../hooks/useDeleteClinic';
import { useHistory } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { DangerButton, HealtheraButton } from '../../../../../healtheraComponents';
import useUpdateClinic from '../../hooks/useUpdateClinic';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import styled from 'styled-components';
import ClinicSpotlightSelector from './ClinicSpotlightSelector';

const ModifyClinicMainTab = ({ form, clinic }: ExistingClinicFormProps) => {
  return (
    <StyledContainer>
      <h2>&nbsp;</h2>
      <CreateClinicBody form={form} />
      <ClinicSpotlightSelector form={form} clinicId={clinic._id} />
      <ModifyClinicFooter clinicId={clinic._id} form={form} />
    </StyledContainer>
  );
};

const ModifyClinicFooter = ({ form, clinicId }: ClinicFormAndIdProps) => {
  return (
    <StickyFooter>
      <StyledButtonsContainer>
        <DeleteClinicButton clinicId={clinicId} />
        <SaveClinicButton clinicId={clinicId} form={form} />
      </StyledButtonsContainer>
    </StickyFooter>
  );
};

const StickyFooter = styled(StyledPaperContainer)`
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: #fff;
  border-top: 1px solid #e0e0e0;
  margin-top: 16px;
`;

const DeleteClinicButton = ({ clinicId }: ClinicIdProps) => {
  const { deleteClinic, isLoading } = useDeleteClinic();
  const history = useHistory();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const handleDelete = async () => {
    closeDialog();
    await deleteClinic(clinicId);
    enqueueSnackbar('Clinic deleted', { variant: 'success' });
    history.push('/clinics');
  };

  return (
    <>
      <DangerButton onClick={openDialog} disabled={isLoading}>
        Delete clinic
      </DangerButton>
      
      <DeleteConfirmationDialog 
        open={isDialogOpen}
        onClose={closeDialog}
        onConfirm={handleDelete}
      />
    </>
  );
};

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmationDialog = ({ open, onClose, onConfirm }: DeleteConfirmationDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
    >
      <DialogTitle id="delete-dialog-title">Delete Clinic</DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-dialog-description">
          Are you sure you want to delete this clinic? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ gap: '10px' }}>
        <HealtheraButton onClick={onClose}>
          Cancel
        </HealtheraButton>
        <DangerButton onClick={onConfirm} autoFocus>
          Delete
        </DangerButton>
      </DialogActions>
    </Dialog>
  );
};

const SaveClinicButton = ({ clinicId, form }: ClinicFormAndIdProps) => {
  const { handleSave, isLoading } = useHandleSaveUpdate({ clinicId, form });

  const isAnyFieldChanged = form.formState.isDirty;
  const shouldDisableSave = isLoading || !isAnyFieldChanged;

  return (
    <HealtheraButton disabled={shouldDisableSave} onClick={form.handleSubmit(handleSave)}>
      Save
    </HealtheraButton>
  );
};

const useHandleSaveUpdate = ({ clinicId, form }: ClinicFormAndIdProps) => {
  const { updateClinic, isLoading } = useUpdateClinic();

  const handleSave = async () => {
    const updatedFields = getUpdatedFields(form);

    await updateClinic([clinicId, updatedFields]);

    form.reset(form.getValues());
    enqueueSnackbar('Clinic updated', { variant: 'success' });
  };

  return { handleSave, isLoading };
};

function getUpdatedFields(form: ClinicFormProps['form']) {
  const dirtyFields = getDirtyFields(form);
  const clinic = form.getValues();

  const keys = new Set(Object.keys(dirtyFields));

  // Always persist the clinic-level promo from this tab: react-hook-form's dirty tracking is
  // unreliable for reordering items in a useFieldArray (hero_benefits / how_it_works.steps), so a
  // pure reorder can be missed by dirtyFields and silently dropped from the payload.
  keys.add('promotional_content');

  // Never send clinic_conditions from the General tab — they're edited on the Conditions tab, and
  // dragging the whole array through the clinic-update validator here can also spuriously trip on
  // its contents. (`conditions` is the empty helper array added by useClinicWithTreatments.)
  keys.delete('clinic_conditions');
  keys.delete('conditions');

  return createObjectWithSpecifiedKeys(clinic, Array.from(keys));
}

function createObjectWithSpecifiedKeys(fromObject: Record<string, any>, keys: string[]) {
  return keys.reduce((acc, key) => {
    acc[key] = fromObject[key];
    return acc;
  }, {});
}

function getDirtyFields(form: ClinicFormProps['form']) {
  return form.formState.dirtyFields;
}

export default ModifyClinicMainTab;
